import { http, httpPaged, type PagedResponse } from '@/services/http';

export interface Analysis {
  id: number;
  name: string;
  description?: string;
  archived?: boolean;
  [key: string]: unknown;
}

export const FILTER_OPERATORS = {
  String: ['!=', '=', 'in', 'contains'],
  Fixnum: ['!=', '=', '<=', '>=', '<', '>'],
  Float: ['!=', '=', '<=', '>=', '<', '>'],
  DateTime: ['!=', '=', '<=', '>=', '<', '>', 'RANGE (inclusive)'],
} as const;
export const AGGREGATE_OPERATORS = ['COUNT', 'COUNT_DISTINCT', 'AVG', 'SUM', 'MIN', 'MAX'] as const;
export const DATE_AGG_OPERATORS = ['Day', 'Month', 'Year'] as const;
export const CHART_TYPES = ['Spreadsheet', 'Bar'] as const;

function projectScope(projectId: number | string): string {
  return `projects/${projectId}/`;
}

export const analysesApi = {
  index(projectId: number | string): Promise<Analysis[]> {
    return http<Analysis[]>({ url: `${projectScope(projectId)}analyses`, method: 'get' });
  },

  indexPaged(
    projectId: number | string,
    page: number,
    per: number,
  ): Promise<PagedResponse<Analysis[]>> {
    return httpPaged<Analysis[]>({
      url: `${projectScope(projectId)}analyses`,
      method: 'get',
      params: { page, per },
    });
  },

  search(projectId: number | string, query: string): Promise<Analysis[]> {
    return http<Analysis[]>({
      url: `${projectScope(projectId)}analyses/search`,
      method: 'get',
      params: { query },
    });
  },

  indexAll(): Promise<Analysis[]> {
    return http<Analysis[]>({ url: 'analyses', method: 'get' });
  },

  get(projectId: number | string, analysisId: number | string): Promise<Analysis> {
    return http<Analysis>({
      url: `${projectScope(projectId)}analyses/${analysisId}`,
      method: 'get',
    });
  },

  create(projectId: number | string, analysis: Partial<Analysis>): Promise<Analysis> {
    return http<Analysis>({
      url: `${projectScope(projectId)}analyses`,
      method: 'post',
      data: analysis,
    });
  },

  update(
    projectId: number | string,
    analysis: Partial<Analysis> & { id: number },
  ): Promise<Analysis> {
    return http<Analysis>({
      url: `${projectScope(projectId)}analyses/${analysis.id}`,
      method: 'patch',
      data: analysis,
    });
  },

  archive(projectId: number | string, analysisId: number | string): Promise<Analysis> {
    return http<Analysis>({
      url: `${projectScope(projectId)}analyses/${analysisId}`,
      method: 'patch',
      data: { archived: true },
    });
  },

  clone(projectId: number | string, analysisId: number | string): Promise<Analysis> {
    return http<Analysis>({
      url: `${projectScope(projectId)}analyses/${analysisId}/clone`,
      method: 'post',
    });
  },

  indexFavorite(userId: number | string): Promise<Analysis[]> {
    return http<Analysis[]>({ url: `users/${userId}/favorites/analyses`, method: 'get' });
  },

  addFavorite(userId: number | string, analysisId: number | string): Promise<unknown> {
    return http({ url: `users/${userId}/favorites/analyses/${analysisId}`, method: 'post' });
  },

  removeFavorite(userId: number | string, analysisId: number | string): Promise<void> {
    return http<void>({
      url: `users/${userId}/favorites/analyses/${analysisId}`,
      method: 'delete',
    });
  },

  newBlank(): Analysis {
    return { id: 0, name: '', description: '' };
  },
};
